import { Box, Button, Card, Divider, Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import { useEffect, useState } from "react";
import EditAttachments from "./edit-attachments";
import { useDispatch, useSelector } from "react-redux";
import { getOrderAttachmentsRequest } from "../../../../redux/orders/slice";
import Loading from "../../../../components/loading/loading";
import { userType } from "../../../../utils/Constant";
import InspectorAttachments from "./inspector-attachments";
import { downloadTemporaryFileRequest } from "../../../../utils/APICalls";
import ErrorNotification from "../../../../components/error-notification/error-notification";

export default function Attachments({ orderId }: any) {
  const dispatch = useDispatch();
  const [attachments, setAttachments] = useState<any[]>([]);
  const [editAttachment, setEditAttachment] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { orderAttachments, loading } = useSelector(
    (state: any) => state.orders
  );
  const { currentUser } = useSelector((state: any) => state.user.user);

  useEffect(() => {
    if (orderId) dispatch(getOrderAttachmentsRequest({ orderId }));
  }, [orderId]); // eslint-disable-line

  useEffect(() => {
    if (orderAttachments) {
      setAttachments(orderAttachments);
    }
  }, [orderAttachments]);

  const downloadAttachment = async (data: any) => {
    setShowLoader(true);
    try {
      const response = await downloadTemporaryFileRequest(data.attachmentKey);
      if (response && response.status === 200) {
        let fileUrl = response.data;

        if (typeof fileUrl === "string") {
          fileUrl = fileUrl.trim();
          if (fileUrl.startsWith('"') && fileUrl.endsWith('"')) {
            fileUrl = JSON.parse(fileUrl);
          }
        }

        const a = document.createElement("a");
        a.href = fileUrl;
        a.download = data?.attachmentName;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else if (response && response.status >= 400) {
        setShowErrorNotification(true);
        setErrorMessage("Failed to download document. Please try again.");
      }
    } catch (error) {
      setShowErrorNotification(true);
      setErrorMessage("Failed to download document. Please try again.");
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <>
      <Card sx={{ mx: 2, my: 3 }}>
        {currentUser?.userType === userType.inspector ? (
          <>
            <InspectorAttachments attachments={attachments} orderId={orderId} />
          </>
        ) : (
          <>
            {editAttachment ? (
              <EditAttachments
                attachments={attachments}
                orderId={orderId}
                closeEdit={() => setEditAttachment(false)}
              />
            ) : (
              <Box
                sx={{
                  mx: 2,
                  my: 3,
                  border: "1px solid #C4C4C4",
                  borderRadius: 1,
                  background: "#FAFAFA",
                }}
              >
                <Box
                  sx={{
                    mt: 2,
                    mx: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="h5">Attachments</Typography>
                  <Button
                    variant="text"
                    startIcon={<EditOutlinedIcon />}
                    onClick={() => setEditAttachment(true)}
                  >
                    Edit
                  </Button>
                </Box>

                <Typography sx={{ ml: 2, mr: 12, mt: 1 }}>
                  these may include instructions or forms you are required to
                  complete and upload.Upload documents required to be completed
                  and submitted with this inspection. If a revision needs to be
                  made to an inspector upload, check the “QC Replacement”
                  checkbox and upload the updated version. Uploading a QC
                  replacement file will be included with results instead of the
                  inspectors upload.
                </Typography>

                <Box sx={{ mx: 2, my: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex",
                    }}
                  >
                    <Typography
                      sx={{
                        width: "calc(100% - 470px)",
                        fontWeight: "550",
                        mr: 2,
                      }}
                    >
                      <b>Document</b>
                    </Typography>
                    <Typography sx={{ width: 150, fontWeight: "550", mr: 2 }}>
                      <b>Inspector Upload?</b>
                    </Typography>
                    <Typography
                      sx={{
                        width: 160,
                        fontWeight: "550",
                        mr: 1,
                        textAlign: "center",
                      }}
                    >
                      <b>Send to Inspector?</b>
                    </Typography>
                    <Typography
                      sx={{
                        width: 160,
                        fontWeight: "550",
                        textAlign: "center",
                      }}
                    >
                      Include with Results?
                    </Typography>
                  </Box>
                  <Divider sx={{ mt: 0.5 }} />
                  {attachments.length === 0 && (
                    <Typography mt={1}>No Documents</Typography>
                  )}
                  {attachments.map((attachment) => (
                    <Box
                      key={attachment?.attachmentKey}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        mt: 1,
                      }}
                    >
                      <Typography
                        component="span"
                        color="primary"
                        sx={{
                          width: "calc(100% - 470px)",
                          mr: 2,
                          cursor: "pointer",
                        }}
                        onClick={() => downloadAttachment(attachment)}
                      >
                        {attachment?.attachmentName}
                      </Typography>
                      <Box sx={{ width: 150, mr: 2, textAlign: "center" }}>
                        {attachment?.isAddedByInspector ? (
                          <CheckOutlinedIcon color="primary" />
                        ) : (
                          <CloseOutlinedIcon
                            sx={{ color: "rgba(0, 0, 0, 0.38)" }}
                          />
                        )}
                      </Box>
                      <Box
                        sx={{
                          width: 160,
                          mr: 1,
                          textAlign: "center",
                        }}
                      >
                        {attachment?.sendToInspector ? (
                          <CheckOutlinedIcon color="primary" />
                        ) : (
                          <CloseOutlinedIcon
                            sx={{ color: "rgba(0, 0, 0, 0.38)" }}
                          />
                        )}
                      </Box>
                      <Typography sx={{ width: 160, textAlign: "center" }}>
                        {attachment?.includeWithResult ? (
                          <CheckOutlinedIcon color="primary" />
                        ) : (
                          <CloseOutlinedIcon
                            sx={{ color: "rgba(0, 0, 0, 0.38)" }}
                          />
                        )}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}
      </Card>

      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          message={errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
      )}

      {(loading || showLoader) && <Loading />}
    </>
  );
}
